import React, { useState, useCallback, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { supabase } from '../lib/supabaseClient';
import { useNavigate } from 'react-router-dom';
import { logPaywallEvent, getDeviceType } from '../utils/paywallAnalytics';
import { useAuth } from '../contexts/AuthContext';

const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

export default function PaywallPage() {
  const navigate = useNavigate();
  const { grantLifetimeAccess } = useAuth();
  const [showCheckout, setShowCheckout] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const pollRef = useRef(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreMessage, setRestoreMessage] = useState(null);

  // Track time on page for CTA and abandon events
  const mountTimeRef = useRef(Date.now());
  // Track time checkout was opened for abandon event
  const checkoutOpenedAtRef = useRef(null);
  // Track scroll depth and whether CTA was clicked (to avoid double-fire on unmount)
  const maxScrollDepthRef = useRef(0);
  const ctaClickedRef = useRef(false);

  // ── paywall_viewed (once on mount) ──────────────────────────────────────────
  useEffect(() => {
    // sessionStorage set by FigChat on init — reliable in SPA, unlike document.referrer
    const source = sessionStorage.getItem('paywall_source') || 'direct';
    logPaywallEvent('paywall_viewed', {
      device_type: getDeviceType(),
      source,
    });
  }, []);

  // ── Scroll depth tracking + paywall_interaction on exit ──────────────────────
  useEffect(() => {
    const getScrollDepth = () => {
      const el = document.documentElement;
      const scrollable = el.scrollHeight - el.clientHeight;
      if (scrollable <= 0) return 100;
      return Math.round(((el.scrollTop || document.body.scrollTop) / scrollable) * 100);
    };

    const getTimeBucket = (seconds) => {
      if (seconds < 3) return '0-3s';
      if (seconds < 10) return '3-10s';
      return '10s+';
    };

    const onScroll = () => {
      const depth = getScrollDepth();
      if (depth > maxScrollDepthRef.current) maxScrollDepthRef.current = depth;
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      // Only fire if the user left without clicking CTA (no double-counting)
      if (!ctaClickedRef.current) {
        const seconds = Math.floor((Date.now() - mountTimeRef.current) / 1000);
        logPaywallEvent('paywall_interaction', {
          scroll_depth: maxScrollDepthRef.current,
          time_bucket: getTimeBucket(seconds),
          seconds_on_page: seconds,
          converted: false,
        });
      }
    };
  }, []);

  // ── Detect return from Stripe after payment ──────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      setIsPolling(true);
      let attempts = 0;

      const poll = async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session?.access_token) return;

          const res = await fetch(`${BACKEND_URL}/api/user/status`, {
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          if (res.ok) {
            const data = await res.json();
            if (data.has_lifetime_access) {
              clearInterval(pollRef.current);
              grantLifetimeAccess();
              navigate('/roadmap', { replace: true });
              return;
            }
          }
        } catch {
          // keep polling
        }
        attempts++;
        if (attempts >= 15) {
          clearInterval(pollRef.current);
          setIsPolling(false);
          setPollTimedOut(true);
        }
      };

      poll();
      pollRef.current = setInterval(poll, 2000);
      return () => clearInterval(pollRef.current);
    }
  }, [navigate]);

  // ── Restore Access (for users who already paid but are blocked) ──────────────
  // Calls /billing/restore-access which verifies directly against Stripe and
  // self-heals the DB if a completed payment is found — covers webhook failures.
  const handleRestoreAccess = async () => {
    setIsRestoring(true);
    setRestoreMessage(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setRestoreMessage({ type: 'error', text: 'Please sign in first, then try again.' });
        return;
      }
      const res = await fetch(`${BACKEND_URL}/api/billing/restore-access`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.has_lifetime_access) {
          grantLifetimeAccess();
          navigate('/roadmap', { replace: true });
          return;
        }
      }
      setRestoreMessage({
        type: 'info',
        text: "We couldn't find a completed payment on your account. Please email support@figcareers.com with your payment receipt and we'll sort it out right away.",
      });
    } catch {
      setRestoreMessage({
        type: 'info',
        text: "We couldn't reach our servers. Please email support@figcareers.com and we'll restore your access manually.",
      });
    } finally {
      setIsRestoring(false);
    }
  };

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const getTimeBucket = (seconds) => {
    if (seconds < 3) return '0-3s';
    if (seconds < 10) return '3-10s';
    return '10s+';
  };

  const handleCTAClick = () => {
    const secondsOnPage = Math.floor((Date.now() - mountTimeRef.current) / 1000);
    ctaClickedRef.current = true;
    logPaywallEvent('paywall_cta_clicked', { seconds_on_page: secondsOnPage });
    logPaywallEvent('paywall_interaction', {
      scroll_depth: maxScrollDepthRef.current,
      time_bucket: getTimeBucket(secondsOnPage),
      seconds_on_page: secondsOnPage,
      converted: true,
    });
    setShowCheckout(true);
  };

  const handleCheckoutMounted = () => {
    checkoutOpenedAtRef.current = Date.now();
    logPaywallEvent('checkout_opened', {
      seconds_from_page_load: Math.floor((Date.now() - mountTimeRef.current) / 1000),
    });
  };

  const handleBackFromCheckout = () => {
    const secondsInCheckout = checkoutOpenedAtRef.current
      ? Math.floor((Date.now() - checkoutOpenedAtRef.current) / 1000)
      : 0;
    logPaywallEvent('checkout_abandoned', { seconds_in_checkout: secondsInCheckout });
    setShowCheckout(false);
  };

  // ── Stripe client secret fetcher ─────────────────────────────────────────────
  const fetchClientSecret = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) throw new Error('Not authenticated');

    const res = await fetch(`${BACKEND_URL}/api/billing/create-lifetime-checkout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to create checkout session');
    const data = await res.json();
    return data.client_secret;
  }, []);

  const options = { fetchClientSecret };

  // ── Post-payment timeout state ───────────────────────────────────────────────
  if (pollTimedOut) {
    return (
      <div className="relative w-full h-screen overflow-hidden">
        <img src="/Paywall_Backdrop_Carreer_Map.png" alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 flex items-center justify-center w-full h-full">
          <div className="bg-white rounded-2xl shadow-2xl p-10 flex flex-col items-center text-center max-w-sm w-full mx-4">
            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center mb-5">
              <svg className="w-5 h-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Payment received — still processing
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              Your payment went through but access is taking longer than usual to activate. Try refreshing in a few seconds.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="w-full py-3 rounded-xl text-white font-semibold text-sm"
              style={{ backgroundColor: '#007AFF' }}
            >
              Refresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Post-payment loading state ────────────────────────────────────────────────
  if (isPolling) {
    return (
      <div className="relative w-full h-screen overflow-hidden">
        <img
          src="/Paywall_Backdrop_Carreer_Map.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 flex items-center justify-center w-full h-full">
          <div className="bg-white rounded-2xl shadow-2xl p-10 flex flex-col items-center text-center max-w-sm w-full mx-4">
            <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin mb-5" style={{ borderColor: '#007AFF', borderTopColor: 'transparent' }} />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Setting up your access...</h2>
            <p className="text-sm text-gray-500 mb-4">Your payment was successful. Hang tight for just a second.</p>
            <p className="text-sm text-gray-400 italic" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              * we hope you now have all the tools to help you realize there's nothing stopping you from having an insane career!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-screen overflow-hidden">
      {/* Background — already blurred by design */}
      <img
        src="/Paywall_Backdrop_Carreer_Map.png"
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        aria-hidden="true"
      />

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/40" />

      {/* Centered card */}
      <div className="relative z-10 flex items-center justify-center w-full h-full px-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">

          {!showCheckout ? (
            /* Pricing panel */
            <div className="p-8 flex flex-col items-center text-center">
              <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: '#007AFF' }}>
                Founding Member Offer
              </p>
              <h1 className="text-2xl font-bold text-gray-900 mb-1" style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: '-0.02em' }}>
                Your Career Roadmap Is Ready
              </h1>
              <p className="text-gray-500 text-sm mb-6">
                Get lifetime access to your personalized career roadmap, road maps, and career trajectory AI chat.
              </p>

              {/* Pricing */}
              <div className="flex items-center gap-4 mb-8">
                <div className="relative inline-block leading-none">
                  <span className="text-4xl font-extrabold text-gray-300">$12</span>
                  <svg
                    className="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
                    preserveAspectRatio="none"
                  >
                    <line
                      x1="4%" y1="92%"
                      x2="96%" y2="8%"
                      stroke="#d1d5db"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <span className="text-3xl font-bold" style={{ color: '#007AFF' }}>$7.98</span>
              </div>

              {/* Feature list */}
              <ul className="text-left text-sm text-gray-600 space-y-2 mb-8 w-full">
                {[
                  'Full personalized career roadmap',
                  'AI-powered role suggestions & paths',
                  'What-if career scenario editor',
                  'Role deep-dives & salary data',
                  'All future updates included',
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={handleCTAClick}
                className="w-full py-3 rounded-xl text-white font-semibold text-base transition-colors"
                style={{ backgroundColor: '#007AFF' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#0066D6'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#007AFF'}
              >
                Get Access for $7.98
              </button>
              <p className="text-xs text-gray-400 mt-3">Secure payment powered by Stripe</p>

              {/* Already paid? Restore access */}
              <div className="mt-5 pt-4 border-t border-gray-100 w-full text-center">
                <button
                  onClick={handleRestoreAccess}
                  disabled={isRestoring}
                  className="text-xs text-gray-400 hover:text-gray-600 underline disabled:opacity-50"
                >
                  {isRestoring ? 'Checking your account…' : 'Already have access? Click here to restore it'}
                </button>
                {restoreMessage && (
                  <p className={`mt-2 text-xs ${restoreMessage.type === 'error' ? 'text-red-500' : 'text-gray-500'}`}>
                    {restoreMessage.text}
                  </p>
                )}
              </div>
            </div>
          ) : (
            /* Stripe Embedded Checkout */
            <div className="p-4">
              <button
                onClick={handleBackFromCheckout}
                className="mb-3 text-sm text-gray-400 hover:text-gray-600 flex items-center gap-1"
              >
                ← Back
              </button>

              {!stripePromise ? (
                <div className="py-10 flex flex-col items-center text-center gap-3">
                  <p className="text-sm text-gray-600">Checkout is not configured for this environment yet.</p>
                  <p className="text-xs text-gray-400">Set REACT_APP_STRIPE_PUBLISHABLE_KEY in your frontend .env to enable payments.</p>
                </div>
              ) : (
                <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
                  <EmbeddedCheckout onReady={handleCheckoutMounted} />
                </EmbeddedCheckoutProvider>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
